import { Box } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import DefaultLoader from "components/common/DefaultLoader";
import OpenClosedBadge from "components/common/OpenClosedBadge";
import { makePrettyNumberColDef } from "components/common/dataGrid/prettyNumberColumn";
import * as labels from "config.labels";
import { CREATE_CLAIMS_COVERAGE_LIST } from "flags";
import { useSelector } from "react-redux";
import * as claimsService from "services/claimsService";
import { useTranslator } from "store/hooks/staticDataHooks";
import useDebouncedValue from "store/hooks/useDebouncedValue";
import * as claimsSelectors from "store/selectors/input/claims/claimsSelectors";
import * as userSelectors from "store/selectors/user/userSelectors";
import * as utils from "utils";

const usDate = (value) => utils.usDateFormat(value);

const getColDefinitions = ({ componentVisibility, translateLabel }) => {
  return [
    {
      field: "_DATE_OF_LOSS",
      headerName: translateLabel(labels.TOP_CLAIMS_DATE_OF_LOSS, {
        default: "Date",
      }),
      align: "right",
      headerAlign: "right",
      valueFormatter: usDate,
      disableColumnMenu: true,
      flex: 0,
      minWidth: 120,
    },
    makePrettyNumberColDef({
      field: "_PAID",
      headerName: translateLabel(labels.TOP_CLAIMS_PAID, {
        default: "Paid",
      }),
      prefix: "$",
      disableColumnMenu: true,
      flex: 0,
    }),
    makePrettyNumberColDef({
      field: "_RESERVED",
      headerName: translateLabel(labels.TOP_CLAIMS_RESERVED, {
        default: "Reserved",
      }),
      prefix: "$",
      disableColumnMenu: true,
      flex: 0,
    }),
    makePrettyNumberColDef({
      field: "_TOTAL_LOSS",
      headerName: translateLabel(labels.TOP_CLAIMS_TOTAL_LOSS, {
        default: "Reported",
      }),
      prefix: "$",
      disableColumnMenu: true,
      flex: 0,
    }),
    makePrettyNumberColDef({
      field: "_ULTIMATE_INCURRED",
      headerName: translateLabel(labels.TOP_CLAIMS_ULTIMATE_INCURRED, {
        default: "Ultimate",
      }),
      prefix: "$",
      disableColumnMenu: true,
      flex: 0,
    }),
    {
      field: "_COVERAGE",
      headerName: translateLabel(labels.TOP_CLAIMS_COVERAGE, {
        default: "LOB",
      }),
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      flex: 0,
    },
    {
      field: "_CLAIMANT_NAME",
      headerName: translateLabel(labels.TOP_CLAIMS_CLAIMANT_NAME, {
        default: "Claimant",
      }),
      disableColumnMenu: true,
      flex: 1,
    },
    {
      field: "_DESCRIPTION",
      headerName: translateLabel(labels.TOP_CLAIMS_DESCRIPTION, {
        default: "Description",
      }),
      disableColumnMenu: true,
      flex: 2,
    },
    {
      field: "_OPEN_CLOSED",
      headerName: translateLabel(labels.TOP_CLAIMS_OPEN_CLOSED, {
        default: "Status",
      }),
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      flex: 0,
      renderCell: (params) => <OpenClosedBadge status={params.value} />,
    },
    {
      field: "_DATE_CLOSED",
      headerName: translateLabel(labels.TOP_CLAIMS_DATE_CLOSED, {
        default: "Date Closed",
      }),
      align: "right",
      headerAlign: "right",
      disableColumnMenu: true,
      valueFormatter: usDate,
      flex: 0,
      minWidth: 120,
    },
  ].filter(
    (defn) => componentVisibility?.[`<Print>.topClaims.${defn.field}`] ?? true
  );
};

const LoadingOverlay = () => (
  <Box
    sx={{
      position: "relative",
      top: 56,
      display: "block",
      textAlign: "center",
      verticalAlign: "center",
    }}
  >
    <DefaultLoader size={60} />
  </Box>
);

const TopClaims = ({ fullWidth }) => {
  const translateLabel = useTranslator("uiLabels");
  const componentVisibility =
    useSelector(userSelectors.selectUserConfig)?.componentVisibility ?? {};

  const createCoverageList =
    componentVisibility[CREATE_CLAIMS_COVERAGE_LIST] ?? true;

  const claimsQuery = useDebouncedValue(
    useSelector(claimsSelectors.selectInputClaims)
  );
  const lossYears = useSelector(
    (state) => state.input?.claims?.lossYears || []
  );
  const {
    currentData: claims,
    isFetching: isClaimsLoading,
  } = claimsService.useClaimsQuery({
    claimsQuery,
    limit: 100,
    years: lossYears,
    includeExcluded: false,
  });

  const mapClaims = (x, index) => {
    const res = { ...x, id: index };
    const cl = res["_COVERAGE_LIST"];
    if (createCoverageList && cl && cl.length > 1) {
      res["_COVERAGE"] += "(" + cl.join(", ") + ")";
    }
    return res;
  };

  const topClaims = (claims ?? []).slice(0, 10).map(mapClaims);
  return (
    <div
      style={{
        width: fullWidth ? "100%" : "860px",
        marginBottom: 48,
        display: "block",
      }}
    >
      <span className={"print-label"}>
        {translateLabel(labels.TOP_CLAIMS_TITLE, {
          default: "Top Claims",
        })}
      </span>
      <DataGrid
        rows={topClaims}
        columns={getColDefinitions({ componentVisibility, translateLabel })}
        pageSize={10}
        className={"print-claims-table"}
        rowHeight={30}
        autoHeight={true}
        hideFooter={true}
        loading={isClaimsLoading}
        slots={{ loadingOverlay: LoadingOverlay }}
      />
    </div>
  );
};

export default TopClaims;
